import type { List, Status, StatusType } from './types.ts';

export const STATUS_GROUP_ORDER: readonly StatusType[] = [
  'not_started',
  'active',
  'done',
  'closed',
];

export const STATUS_GROUP_LABEL: Record<StatusType, string> = {
  not_started: 'Not started',
  active: 'Active',
  done: 'Done',
  closed: 'Closed',
};

/** "Complete" means the status belongs to the Done or Closed group. */
export function isCompleteType(type: StatusType | null | undefined): boolean {
  return type === 'done' || type === 'closed';
}

type StatusLike = Pick<Status, 'id' | 'type' | 'position'> &
  Partial<Pick<Status, 'space_id' | 'list_id' | 'deleted_at'>>;

/** Sort statuses the way the UI shows them: by group, then by position within the group. */
export function sortStatuses<S extends StatusLike>(statuses: readonly S[]): S[] {
  return [...statuses].sort(
    (a, b) =>
      STATUS_GROUP_ORDER.indexOf(a.type) - STATUS_GROUP_ORDER.indexOf(b.type) ||
      a.position - b.position,
  );
}

/**
 * The statuses a list uses: its own set when it overrides, otherwise its space's defaults.
 * Deleted statuses are ignored.
 */
export function resolveListStatuses<S extends StatusLike>(
  list: Pick<List, 'id' | 'space_id' | 'override_statuses'>,
  statuses: readonly S[],
): S[] {
  const alive = statuses.filter((s) => !s.deleted_at);
  const own = alive.filter((s) => s.list_id === list.id);
  const scoped =
    list.override_statuses && own.length > 0
      ? own
      : alive.filter((s) => s.space_id === list.space_id);
  return sortStatuses(scoped);
}

/** The status after `currentId` in display order (ClickUp's "next status" arrow). */
export function nextStatus<S extends StatusLike>(
  currentId: string,
  ordered: readonly S[],
): S | null {
  const i = ordered.findIndex((s) => s.id === currentId);
  if (i < 0) return ordered[0] ?? null;
  return ordered[i + 1] ?? null;
}

export function firstStatusOfType<S extends StatusLike>(
  ordered: readonly S[],
  type: StatusType,
): S | null {
  return sortStatuses(ordered).find((s) => s.type === type) ?? null;
}

/** The status a new task gets: first "not started", else the first status. */
export function defaultOpenStatus<S extends StatusLike>(ordered: readonly S[]): S | null {
  return firstStatusOfType(ordered, 'not_started') ?? sortStatuses(ordered)[0] ?? null;
}

/** The status used by the ✓ "complete" button: first Closed, else first Done. */
export function completeStatus<S extends StatusLike>(ordered: readonly S[]): S | null {
  return firstStatusOfType(ordered, 'closed') ?? firstStatusOfType(ordered, 'done');
}
